// Third-party
import React from 'react';
import classNames from 'classnames';

// Utils
import { FormattedMessage, formatMessage, formatOrdinalSuffix, formatLocalizedString } from '@/utils/i18n';
import { RankingLimit, ESGColourDefinition } from '@/enums/Esg';
import { HoldingIcon } from '@/views/components/common';
import InfoTooltip from '@/views/components/tooltips/InfoTooltip';
import { Tooltip } from '@/views/components/tooltips';
import ComparisonItem from '@/types/comparison-item';
import ThemeMode from '@/types/theme-mode';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  selectedItems: Array<ComparisonItem>;
  theme?: ThemeMode;
};

const ESGRanking = ({ selectedItems, theme }: Props) => {
  const barLimit = 10;
  const decimalsAllowed = 0;
  const firstItem = selectedItems && selectedItems[0];
  const secondItem = selectedItems && selectedItems[1];

  const getRankingColor = (ranking?: number) => {
    if (!ranking) return '';
    if (ranking > RankingLimit.HIGH) return ESGColourDefinition.RANKING_MODERATE;
    if (ranking >= RankingLimit.MODERATE) return ESGColourDefinition.RANKING_HIGH;
    return ESGColourDefinition.RANKING_VERY_HIGH;
  };

  const calcPercentagePosition = (value: number, isBar?: boolean) => {
    if (value <= 0) {
      return !isBar ? barLimit : 0;
    }

    if (value >= 100) {
      return 100 - barLimit;
    }

    return barLimit + (value * (100 - barLimit * 2)) / 100;
  };

  const renderRankingGraph = (value?: number, type?: string) => {
    const roundedValue = value ? +value.toFixed(decimalsAllowed) : undefined;
    const rankingColor = getRankingColor(value);
    return (
      <div className={`esg-ranking-content ${!value ? 'esg-ranking-empty' : ''}`}>
        <h4
          style={{
            borderColor: rankingColor,
            marginLeft: value ? `${calcPercentagePosition(value)}%` : '50%',
          }}
        >
          {type && <FormattedMessage tagName="small" id={`ESGComparisonTool.Ranking.${type}`} />}
          <strong>
            {roundedValue || formatMessage('Common.NotApplicable')}
            <sup>{roundedValue ? formatOrdinalSuffix(roundedValue) : ''}</sup>
          </strong>
          <span className="position-line" style={{ borderColor: rankingColor }} />
        </h4>
        <div className="esg-ranking-percentage">
          <span
            className="esg-ranking-percentage-bar"
            style={{
              backgroundColor: rankingColor,
              marginLeft: value ? `${calcPercentagePosition(value, true)}%` : '0%',
            }}
          />
        </div>
      </div>
    );
  };

  const renderComparingGraph = (options?: ComparisonItem[]) => {
    if (!options) {
      return null;
    }
    const rawOptions = options.slice();

    rawOptions.sort((a, b) => {
      if (a.esg?.globalRank === undefined) {
        return 1;
      }
      if (b.esg?.globalRank === undefined) {
        return -1;
      }
      return b.esg?.globalRank - a.esg?.globalRank;
    });

    const [highest, lowest] = rawOptions;

    const getComparisonColor = (item?: ComparisonItem) => {
      // If they are identical in value but not N/A
      if (highest.esg?.globalRank && lowest.esg?.globalRank && highest.esg?.globalRank === lowest.esg?.globalRank) {
        return '#B1B1B1';
      }
      if (item && item.esg?.globalRank) {
        return item.color;
      }
      return 'transparent';
    };

    return (
      <div
        className="graph-containers"
        style={{ '--highest-color': getComparisonColor(highest), '--lowest-color': getComparisonColor(lowest) } as React.CSSProperties}
      >
        <div className="esg-ranking-content">
          <h4
            style={{
              marginLeft: highest.esg?.globalRank ? `${calcPercentagePosition(highest.esg?.globalRank)}%` : '50%',
            }}
            className="highest"
          >
            <div className="container">
              <Tooltip message={formatLocalizedString(highest.name)}>
                <span>
                  <HoldingIcon type={highest.type} color={highest.color} />
                </span>
              </Tooltip>
              <span className={`ranking ${!highest.esg?.globalRank ? 'not-applicable' : ''}`}>
                {highest.esg?.globalRank ? highest.esg?.globalRank.toFixed(decimalsAllowed) : formatMessage('Common.NotApplicable')}
                <sup>{highest.esg?.globalRank ? formatOrdinalSuffix(+highest.esg?.globalRank.toFixed(decimalsAllowed)) : ''}</sup>
              </span>
            </div>
            <span className="position-line" />
          </h4>
          <h4
            className="lowest"
            style={{
              marginLeft: lowest.esg?.globalRank ? `${calcPercentagePosition(lowest.esg?.globalRank)}%` : '50%',
            }}
          >
            <div className="container">
              <Tooltip message={formatLocalizedString(lowest.name)}>
                <span>
                  <HoldingIcon type={lowest.type} color={lowest.color} />
                </span>
              </Tooltip>
              <span className={`ranking ${!lowest.esg?.globalRank ? 'not-applicable' : ''}`}>
                {lowest.esg?.globalRank ? lowest.esg?.globalRank.toFixed(decimalsAllowed) : formatMessage('Common.NotApplicable')}
                <sup>{lowest.esg?.globalRank ? formatOrdinalSuffix(+lowest.esg?.globalRank.toFixed(decimalsAllowed)) : ''}</sup>
              </span>
            </div>
            <span className="position-line" />
          </h4>
          <div className="esg-ranking-percentage">
            <span
              className="esg-ranking-percentage-bar highest"
              style={{
                marginLeft: highest.esg?.globalRank ? `${calcPercentagePosition(highest.esg?.globalRank, true)}%` : '0%',
              }}
            />
            <span
              className="esg-ranking-percentage-bar lowest"
              style={{
                marginLeft: lowest.esg?.globalRank ? `${calcPercentagePosition(lowest.esg?.globalRank, true)}%` : '0%',
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!secondItem ? (
        <div className={classNames('esg-ranking', { [`${theme}`]: theme })}>
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xs">
            <FormattedMessage id="ESGComparisonTool.Ranking.Title" />
          </Typography>
          <div className="esg-ranking-ranks">
            {renderRankingGraph(firstItem?.esg?.peerRank, 'PeerRank')}
            {renderRankingGraph(firstItem?.esg?.globalRank, 'GlobalRank')}
          </div>
          <FormattedMessage tagName="p" id="ESGComparisonTool.Ranking.EducationalText" />
        </div>
      ) : (
        <div className={classNames('esg-ranking-comparison', { [`${theme}`]: theme })}>
          <div className="title">
            <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xs">
              <FormattedMessage id="ESGComparisonTool.Ranking.Title" />
            </Typography>
            <InfoTooltip message={formatMessage('ESGComparisonTool.Ranking.EducationalText')} data-private data-dd-privacy="mask" className="ms-1" />
          </div>
          <div className="esg-ranking-container">
            <div className="esg-no-peer-ranking">
              <FormattedMessage tagName="h3" id="ESGComparisonTool.Ranking.PeerRank" />
              <div className="message">
                <FormattedMessage id="ESGComparisonTool.Ranking.NoPeerComparison" />
              </div>
            </div>
            <div className="esg-global-ranking">
              <FormattedMessage tagName="h3" id="ESGComparisonTool.Ranking.GlobalRank" />
              {renderComparingGraph(selectedItems)}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ESGRanking;
